import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Icons from '../Icons';
import { isNullOrEmpty } from '../../utils/object-utilities';
import { Themes } from '../../style/theme-consts';

import * as S from './styled';

const MoreMenuVert = ({
  options,
  children,
  testId,
  isCloseAfterSelect,
  onSelect,
  disabled,
  mode = Themes.DARK,
  rootSx,
  paperSx,
  transformOrigin,
  anchorOrigin,
  optionOverrideCss,
  onMenuOpen,
  iconSize
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = e => {
    if (disabled) return;
    e.stopPropagation();
    setAnchorEl(e.currentTarget);

    if (onMenuOpen) {
      onMenuOpen();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = item => e => {
    if (disabled || item.disabled) return;
    const newE = { ...e, target: { ...e.target, value: item.value || item } };
    item.onClick?.(newE);
    onSelect?.(item.value || item);
    isCloseAfterSelect && handleClose();
  };

  const compProps = useMemo(
    () => ({
      paper: { classes: { root: mode }, sx: { ...S.POPPER_STYLE, ...paperSx } },
      root: { sx: rootSx }
    }),
    [mode, rootSx, paperSx]
  );

  return (
    <>
      {children ? (
        <S.Element data-testid={testId} onClick={handleClick}>
          {children}
        </S.Element>
      ) : (
        <S.IconButton disableFocusRipple disableRipple data-testid={testId} onClick={handleClick}>
          <Icons.ThreeDots size={iconSize} />
        </S.IconButton>
      )}
      <S.Menu
        open={open}
        slotProps={compProps}
        onClose={handleClose}
        anchorEl={anchorEl}
        transformOrigin={transformOrigin}
        anchorOrigin={anchorOrigin}>
        {options.map((item, index) => {
          if (item.divider) return <S.Divider key={`divider-${index}`} />;
          if (isNullOrEmpty(item.visible) || item.visible) {
            return (
              <S.MenuItem
                key={item.value || item.title || item.label}
                onClick={handleActionClick(item)}
                disabled={item.disabled}
                data-testid={item.testId}
                $overrideCss={optionOverrideCss}
                selected={item.selected}>
                {item.dotColor && <S.ColoredCircle $color={item.dotColor} />}
                {item.label || item.title}
                {item.isLoading && <S.LoadingIcon />}
              </S.MenuItem>
            );
          }
          return null;
        })}
      </S.Menu>
    </>
  );
};

MoreMenuVert.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      label: PropTypes.node,
      value: PropTypes.string,
      onClick: PropTypes.func,
      testId: PropTypes.string,
      disabled: PropTypes.bool,
      visible: PropTypes.bool,
      dotColor: PropTypes.string,
      isLoading: PropTypes.bool,
      divider: PropTypes.bool
    })
  ).isRequired,
  optionOverrideCss: PropTypes.shape({}),
  testId: PropTypes.string,
  mode: PropTypes.oneOf([Themes.DARK, Themes.LIGHT]),
  rootSx: PropTypes.shape({}),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.element]),
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  transformOrigin: PropTypes.shape({
    horizontal: PropTypes.oneOf(['center', 'left', 'right', PropTypes.number]),
    vertical: PropTypes.oneOf(['bottom', 'center', 'top', PropTypes.number])
  }),
  anchorOrigin: PropTypes.shape({
    horizontal: PropTypes.oneOf(['center', 'left', 'right', PropTypes.number]),
    vertical: PropTypes.oneOf(['bottom', 'center', 'top', PropTypes.number])
  }),
  isCloseAfterSelect: PropTypes.bool,
  paperSx: PropTypes.shape({}),
  onMenuOpen: PropTypes.func,
  iconSize: PropTypes.number
};

export default MoreMenuVert;
